import React from 'react';
import { UseFormRegister, FieldErrors } from 'react-hook-form';
import Input from '../../../components/Form/Input';
import Select from '../../../components/Form/Select';
import { InTransactionFormData, DropdownOption, convertToInvoiceCurrency } from './inTransactionTypes';

interface InDeductionSectionProps {
  register: UseFormRegister<InTransactionFormData>;
  errors: FieldErrors<InTransactionFormData>;
  currencyOptions: DropdownOption[];
  hasInvoice: boolean;
  invoiceCurrencyCode?: string;
  deductionCurrencyCode?: string;
  deductionAmountValue?: number;
  deductionRateValue?: number;
}

const InDeductionSection: React.FC<InDeductionSectionProps> = ({
  register,
  errors,
  currencyOptions,
  hasInvoice,
  invoiceCurrencyCode = '',
  deductionCurrencyCode = '',
  deductionAmountValue = 0,
  deductionRateValue = 0,
}) => {
  // Same conversion rules as the payment amount: a deduction in a different
  // currency than the invoice needs a rate to EGP
  const currencyMismatch =
    hasInvoice && !!invoiceCurrencyCode && !!deductionCurrencyCode &&
    invoiceCurrencyCode !== deductionCurrencyCode && deductionAmountValue > 0;
  const rateCurrency = deductionCurrencyCode !== 'EGP' ? deductionCurrencyCode : invoiceCurrencyCode;

  const appliedToInvoice = currencyMismatch
    ? convertToInvoiceCurrency(deductionAmountValue, deductionCurrencyCode, invoiceCurrencyCode, deductionRateValue)
    : deductionAmountValue;
  const showPreview = hasInvoice && deductionAmountValue > 0 && appliedToInvoice > 0 && !!invoiceCurrencyCode;

  return (
    <div className="bg-gray-50 rounded-xl p-6">
      <h3 className="text-lg font-semibold text-gray-800 mb-4 flex items-center">
        <svg className="w-5 h-5 mr-2 text-green-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M18 12H6" />
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
        </svg>
        Invoice Deduction
      </h3>
      {!hasInvoice && (
        <p className="text-sm text-gray-500 italic mb-4">Select an invoice above to apply a deduction against it.</p>
      )}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
        <Input
          label="Deduction Amount"
          type="number"
          step="0.01"
          disabled={!hasInvoice}
          {...register('deductionAmount', {
            valueAsNumber: true,
            min: { value: 0, message: 'Deduction cannot be negative' }
          })}
          error={errors.deductionAmount?.message}
          placeholder="0.00"
        />
        <Select
          label="Deduction Currency"
          disabled={!hasInvoice}
          {...register('deductionCurrency')}
          options={[{ value: '', label: 'Select currency' }, ...currencyOptions]}
          error={errors.deductionCurrency?.message}
        />
        {currencyMismatch && (
          <Input
            label={`Exchange Rate (${rateCurrency} to EGP)`}
            type="number"
            step="0.01"
            required
            {...register('deductionRate', {
              valueAsNumber: true,
              min: { value: 0.01, message: 'Rate must be positive' }
            })}
            error={errors.deductionRate?.message}
            placeholder="e.g., 50.00"
            helperText={deductionCurrencyCode === 'EGP'
              ? `EGP amount ÷ rate = ${invoiceCurrencyCode} deducted from the invoice`
              : undefined}
          />
        )}
      </div>

      {showPreview && (
        <div className="bg-blue-50 border border-blue-200 rounded-lg p-3 mt-4">
          <p className="text-sm text-blue-800">
            Deducted from the invoice:{' '}
            <strong>
              {appliedToInvoice.toLocaleString(undefined, { maximumFractionDigits: 2 })} {invoiceCurrencyCode}
            </strong>
            {currencyMismatch && (
              deductionCurrencyCode === 'EGP'
                ? ` (${Number(deductionAmountValue).toLocaleString()} EGP ÷ ${deductionRateValue})`
                : ` (${Number(deductionAmountValue).toLocaleString()} ${deductionCurrencyCode} × ${deductionRateValue})`
            )}
          </p>
        </div>
      )}
    </div>
  );
};

export default InDeductionSection;
